import { PlusOutlined } from '@ant-design/icons';
import { Button, Dropdown, Flex, MenuProps } from 'antd';
import React, { useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';

import useDispatchSetterStore from '../hooks/useDispatchSetterStore';
import useDynamicSelector from '../hooks/useDynamicSelector';
import { SmxPropsTableContext } from '../providers/propsTableProvider';
import { getSetterTable, ITableState } from '../store/slices/tableSlice';
import {
    FilterTableParamType,
    GroupFilterType,
    StoreGroupFilterType,
    TableColumnOptionsType,
    TableColumnSmxType,
} from '../types';
import { getItemsListForFilter } from './functions';
import useProcessedColumns from './hooks/useProcessedColumns';
import TableFilter from './tableFilter';
import TableGroupFilterSelectAndOr, { DivPlusStyled } from './tableGroupFilterSelectAndOr';

interface ITableGroupFilterArr {
    group: GroupFilterType;
    paramFilter?: FilterTableParamType;
}

const TableGroupFilterArr: React.FC<ITableGroupFilterArr> = ({ group, paramFilter }) => {
    const { nameQuery, prefixStore } = useContext(SmxPropsTableContext);
    const columns: TableColumnSmxType[] = useDynamicSelector('table', 'columns');
    const setterTable = useDispatchSetterStore<ITableState>(getSetterTable(nameQuery + prefixStore));
    const listGroupFilters = useDynamicSelector('table', 'listGroupFilters');
    const options = useProcessedColumns<TableColumnOptionsType>(columns, 'options');

    const columnsKeyAsFilter = columns.filter((column) => paramFilter?.[column.key]).map((column) => column.key);

    const handleMenuClick = (option: TableColumnOptionsType) => () => {
        const newKey = uuidv4();

        setterTable({
            listGroupFilters: {
                ...listGroupFilters,
                [group.key]: {
                    ...listGroupFilters[group.key],
                    listFilters: {
                        ...listGroupFilters[group.key].listFilters,
                        [newKey]: {
                            key: newKey,
                            parentKey: group.key,
                            condition: 'none',
                            value: '',
                            option,
                        },
                    },
                },
            } as StoreGroupFilterType,
        });
    };

    const items: MenuProps['items'] = getItemsListForFilter(options, columnsKeyAsFilter, handleMenuClick);

    const arrayFilters = Object.values(group.listFilters);
    const lastFilter = arrayFilters[arrayFilters.length - 1];
    const isDisabled: boolean = Boolean(arrayFilters.length && (lastFilter.condition === 'none' || !lastFilter.value));

    return (
        <Flex align={'center'} wrap={'wrap'}>
            {arrayFilters.map((filterItem, index) => (
                <Flex key={filterItem.key} style={{ position: 'relative' }} align={'center'}>
                    <TableFilter filterItem={filterItem} paramFilter={paramFilter} />
                    {index !== arrayFilters.length - 1 && <TableGroupFilterSelectAndOr group={group} />}
                </Flex>
            ))}
            {arrayFilters.length && (
                <DivPlusStyled style={{ marginLeft: '1px' }}>
                    <Dropdown
                        key={uuidv4()}
                        menu={{ items }}
                        placement="bottomLeft"
                        arrow={{ pointAtCenter: true }}
                        trigger={['click']}
                    >
                        <Button type="link" disabled={isDisabled}>
                            <PlusOutlined />
                        </Button>
                    </Dropdown>
                </DivPlusStyled>
            )}
        </Flex>
    );
};

export default TableGroupFilterArr;
